import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TableSkeleton } from '@/components/common/DataTable';
import { Skeleton } from '@/components/ui/skeleton';
import { AppVersionMemberTable } from './AppVersionMemberTable';
import type { AppVersionDepartment } from '../types/appVersionCheck.types';

const SECTION_CLASS = 'rounded-xl border bg-card shadow-sm';
const MEMBER_TABLE_COLUMN_COUNT = 8;

interface AppVersionDepartmentSkeletonProps {
  isOpen: boolean;
}

export function AppVersionDepartmentSkeleton({ isOpen }: AppVersionDepartmentSkeletonProps) {
  return (
    <div className={SECTION_CLASS} aria-hidden>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <span className="flex items-center gap-2">
          <Skeleton className="size-4" />
          <Skeleton className="h-5 w-40" />
        </span>
        <Skeleton className="h-3.5 w-28" />
      </div>
      {isOpen ? (
        <div className="border-t p-4">
          <TableSkeleton columnCount={MEMBER_TABLE_COLUMN_COUNT} />
        </div>
      ) : null}
    </div>
  );
}

interface AppVersionDepartmentSectionProps {
  department: AppVersionDepartment;
  defaultOpen?: boolean;
  searchText?: string | undefined;
}

export function AppVersionDepartmentSection({
  department,
  defaultOpen = false,
  searchText,
}: AppVersionDepartmentSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const memberCount = department.areaManagers.reduce(
    (total, areaManager) => total + areaManager.memberCount,
    0,
  );

  return (
    <section className={SECTION_CLASS}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
          <span className="font-semibold">{department.departmentName}</span>
        </span>
        <span className="text-xs text-muted-foreground">
          {department.areaManagers.length} AM · {memberCount} members
        </span>
      </button>

      {isOpen ? (
        <div className="flex flex-col gap-3 border-t p-4">
          {department.areaManagers.map((areaManager) => (
            <div
              key={areaManager.userId ?? 'unassigned'}
              className="rounded-lg border bg-background"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
                <div>
                  <p className="font-medium">{areaManager.name ?? 'No reporting member'}</p>
                  <p className="text-xs text-muted-foreground">
                    {[areaManager.hrEmpId, areaManager.mobile].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">
                  {areaManager.memberCount} members
                </span>
              </div>
              <AppVersionMemberTable members={areaManager.members} searchText={searchText} />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
